import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Eye, CheckCircle2, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Proposta {
  id: string;
  documento_cpf_nome: string | null;
  html_proposta: string;
  clicksign_assinado: string;
  bt_aceite: boolean | null;
  created_at: string;
  modelo_proposta: {
    nome_modelo: string;
  } | null;
}

interface PropostasEnviadasDisplayProps {
  userEmail: string;
}

export default function PropostasEnviadasDisplay({ userEmail }: PropostasEnviadasDisplayProps) {
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposta, setSelectedProposta] = useState<Proposta | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    buscarPropostas();
  }, [userEmail]);

  const buscarPropostas = async () => {
    try {
      setLoading(true);

      // Primeiro buscar TODAS as contratações do usuário (pode haver múltiplas em diferentes orgs)
      const { data: contratacoesData, error: contratacaoError } = await supabase
        .from('contratacao')
        .select('id, org_id')
        .eq('email', userEmail);

      if (contratacaoError) {
        console.error('Erro ao buscar contratações:', contratacaoError);
        setPropostas([]);
        return;
      }

      if (!contratacoesData || contratacoesData.length === 0) {
        console.log('Nenhuma contratação encontrada para este email');
        setPropostas([]);
        return;
      }

      // Buscar propostas de TODAS as contratações encontradas
      const contratacaoIds = contratacoesData.map(c => c.id);
      
      const { data: propostasData, error: propostasError } = await supabase
        .from('contratacao_propostas')
        .select(`
          id,
          documento_cpf_nome,
          html_proposta,
          clicksign_assinado,
          bt_aceite,
          created_at,
          modelo_proposta:modelo_proposta_id (
            nome_modelo
          )
        `)
        .in('contratacao_id', contratacaoIds)
        .order('created_at', { ascending: false });

      if (propostasError) {
        console.error('Erro ao buscar propostas:', propostasError);
        setPropostas([]);
        return;
      }

      console.log('Propostas encontradas:', propostasData?.length || 0);
      setPropostas(propostasData || []);
    } catch (error) {
      console.error('Erro ao buscar propostas:', error);
      setPropostas([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (proposta: Proposta) => {
    const isAssinado = proposta.clicksign_assinado === 'assinado' || 
                       proposta.clicksign_assinado === 'true' || 
                       proposta.bt_aceite === true;

    if (isAssinado) {
      return (
        <Badge className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Assinado
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20">
        <Clock className="w-3 h-3 mr-1" />
        Pendente
      </Badge>
    );
  };

  const handleVisualizarProposta = (proposta: Proposta) => {
    setSelectedProposta(proposta);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Documentos Enviados</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Carregando...</p>
        </CardContent>
      </Card>
    );
  }

  if (propostas.length === 0) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Documentos Enviados
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {propostas.map((proposta) => (
            <div
              key={proposta.id}
              className="flex items-center justify-between p-4 border rounded-lg bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <h4 className="font-medium">
                    {proposta.modelo_proposta?.nome_modelo || proposta.documento_cpf_nome || 'Proposta de Trabalho'}
                  </h4>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {getStatusBadge(proposta)}
                  <span>•</span>
                  <span>
                    Enviado em {format(new Date(proposta.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleVisualizarProposta(proposta)}
                className="ml-4"
              >
                <Eye className="w-4 h-4 mr-2" />
                Visualizar
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedProposta?.modelo_proposta?.nome_modelo || selectedProposta?.documento_cpf_nome || 'Proposta de Trabalho'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {selectedProposta && (
              <div 
                className="prose prose-sm max-w-none dark:prose-invert p-4"
                dangerouslySetInnerHTML={{ __html: selectedProposta.html_proposta }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
